// Smarttix design tokens — single source of color truth for the FE.
// Per feedback_smarttix_navy_palette.md:
//   - Brand palette = chrome/headers/identity
//   - Category palette = per-K rendering only (don't substitute one for the other)
// Per feedback_orange_k4_exclusive.md:
//   - #BC5A00 = K4 only (AA-darkened from #DC6A00)
//   - #B91C1C = Bearbeitungsfehler / system error

export const theme = {
  colors: {
    // ── Brand navy (chrome, headers, accents) ──
    brand: {
      primary: '#000d66',
      dark: '#131740',
      darkSecondary: '#0a1253',
      mid: '#394388',
      light: '#555e99',
      muted: '#666e9c',           // WCAG 1.4.3 — 4.91:1 on white (was #7179aa, 4.19:1 — failed AA at 11px normal)
    },
    // Aliases used by older code paths (kept until B3 sweep completes; remove after Task 16).
    navy: '#000d66',
    navyDark: '#131740',          // was #1c2877 — aligned to smarttix.web globals.css
    navyMid: '#394388',
    navyLight: '#555e99',
    navyMuted: '#666e9c',         // WCAG 1.4.3 — 4.91:1 on white (alias for brand.muted)

    // ── Demo-only accent (Simulierte Systemdaten button) ──
    // Deliberately off the navy brand axis so the demo affordance reads as distinct from
    // production actions; also distinct from the escalation purples. Never production UI. Spec 2026-07-08.
    demoAccent: '#6D3BD1',
    demoAccentHover: '#5c31b0',

    // ── Category palette (per-K identity tokens) ──
    categories: {
      K0:  '#171937',
      K1:  '#06105A',
      K2:  '#313A77',
      K3A: '#4A517F',
      K3B: '#6B7080',  // WCAG 1.4.3 — 5.1:1 with white badge text (was #7B7F90, 3.97:1 — failed AA at 10px bold)
      K4:  '#BC5A00',  // K4 orange — darkened from #DC6A00 for WCAG-AA (white badge text 4.56:1 @10px; #DC6A00 was 3.44:1, small-text fail). Semantically exclusive per feedback_orange_k4_exclusive
      KU:  '#6B6F7F',
    },

    // ── WIM handling palette (role=wim only; H1–H4 identity tokens, operator-locked 2026-08-05) ──
    // Semantics drive the hues: forward acts outward (green) · internal stays on the brand navy
    // axis · archive is neutral · review needs attention WITHOUT alarm — H4 is therefore
    // deliberately NOT the K4 orange (#BC5A00 stays K4-exclusive) and not the error red.
    // All four hold WCAG-AA with white badge text (guarded in themeContrast.test).
    wimHandling: {
      H1: '#0A6B3D',  // Weiterleiten
      H2: '#0A1253',  // WIM-intern
      H3: '#4A4F63',  // Ablegen
      H4: '#8A5A00',  // Prüfen
    },

    // ── VNB marktprozess palette (role=vnb only; never used for gMSB K-maps) ──
    // einspeisung joined the served schema at content revision v5 (2026-07) and never got an entry
    // here — every einspeisung row rendered the navyMid fallback until 2026-08-19. unbestimmt (schema
    // v9) is the role's UNCLASSIFIED BUCKET (the fail-closed abstention value, excluded from performance views) and
    // deliberately takes KU's hue: one "could not tell" colour across roles, same as `unclassified`.
    marktprozess: {
      versorgerwechsel: '#2563a8', netzabrechnung: '#0a1b6b', messwerte: '#1e3a8a',
      bilanzierung: '#41748f', stammdaten: '#3f4f99', einspeisung: '#1b7a5a', edifact_transport: '#5566a0',
      geraetewechsel: '#2f7d8a', marktpartner_vertrag: '#6b7080', sonstiges: '#9095b3',
      unbestimmt: '#6B6F7F',   // = colors.categories.KU / colors.unclassified — the unclassified-bucket hue
    },

    // ── Reply-engine hand-off zones (VNB D3, GRÜN/GELB/ROT doctrine — exactly three) ──
    // GELB is deliberately NOT the K4 orange (#BC5A00, reserved) and ROT is deliberately
    // NOT the Bearbeitungsfehler red (#B91C1C): zone = hand-off semantics, not error/danger.
    zone: {
      gruen: { fg: '#1D6A3A', bg: '#E7F3EC', border: '#BFDECB' },
      gelb: { fg: '#7A5C00', bg: '#FAF3D9', border: '#E5D28A' },
      rot: { fg: '#8F1C2E', bg: '#F9E8EA', border: '#E5B8BF' },
    },

    // ── Escalation palette (VNB eskalationsstufe 1–3) ──
    eskalation: {
      s1: { fg: '#7e3a9c', bg: '#efe4f5', border: '#d9bfe6' },   // Stufe 1 = outline pill
      s2: { fg: '#ffffff', bg: '#8b2fb0', border: '#8b2fb0' },   // Stufe 2 = solid fill
      s3: { fg: '#ffffff', bg: '#5b1d78', border: '#5b1d78' },   // Stufe 3 = solid fill
    },

    // ── Mode-color family. Active = navy anchor. rejections/archived have light|dark variants
    // selected by the per-user palette preference (PaletteModeContext). See spec 2026-06-16. ──
    mode: {
      active: {
        card: '#000D66', tile: '#E8EBF4', fill: null as string | null, tileText: '#000D66',
        chrome: '#FFFFFF', bigText: '#FFFFFF', pillOnBg: '#E8EBF4', pillOnText: '#000D66',
        pillOffBorder: 'rgba(255,255,255,0.45)', // was 0.35 → 2.92:1; 0.45 → 4.08:1 (WCAG 1.4.11 ✓)
      },
      rejections: {
        light: { card: '#8B91B9', tile: '#EBEBF2', fill: '#B9BDD5', tileText: '#565E8F', chrome: '#000D66', bigText: '#FFFFFF', pillOnBg: '#565E8F', pillOnText: '#FFFFFF', pillOffBorder: 'rgba(0,13,102,0.65)' }, // was 0.4 → 2.04:1; 0.65 → 3.29:1 ✓
        dark:  { card: '#515982', tile: '#E8EBF4', fill: '#B9BDD5', tileText: '#5C6383', chrome: '#FFFFFF', bigText: '#FFFFFF', pillOnBg: '#E8EBF4', pillOnText: '#515982', pillOffBorder: 'rgba(255,255,255,0.5)' },
      },
      archived: {
        light: { card: '#BEBEC9', text: '#000D66' },
        dark:  { card: '#57555C', text: '#FFFFFF' },
      },
    },

    // ── Semantic ──
    accent: '#000d66',
    textSecondary: '#5C617F',     // WCAG 1.4.3 — 5.97:1 on white; FAILS on navy bg, use textOnDark there
    textOnDark: '#a8acca',        // WCAG 1.4.3 — 7.54:1 on navy #000d66, 7.69:1 on navyDark #131740
    borderLight: '#c6c9dd',
    bgLight: '#e3e4ee',
    bgPage: '#f5f6fa',
    white: '#ffffff',

    focusRing: '#000d66',         // WCAG 2.4.7 — visible focus indicator; brand navy, 16.81:1 on white
    success: '#1d6a3a',           // green for positive deltas (PDF/FE convention)
    error: '#B91C1C',             // Bearbeitungsfehler red — aligned to PDF lock (was #dc2626)
    warning: '#BC5A00',           // K4 orange — aligned to AA-darkened K4 (#DC6A00→ now #BC5A00; orig #d97706); never use as a generic warning
    deltaNegative: '#88661c',     // muted amber — for "delta worsened" without conflicting with K4/Bearbeitungsfehler
    neutral: '#6b7280',           // Neutral slice in sentiment chart (archive-mode mute now uses mode.archived)

    // ⚠ Same hue as categories.K4. Pre-existing: PII-reveal borders and PanelSection collapse
    // arrows already render this orange; feedback_orange_k4_exclusive reserves it for K4.
    // Flagged to the operator at plan close — this token only NAMES the current state.
    accentOrange: '#BC5A00',
  },
  // Status strip colors — navy palette shades.
  statusStrip: {
    eingereicht: '#394388',
    offen: '#000d66',
    genehmigt: '#000044',
    error: '#B91C1C',             // aligned to Bearbeitungsfehler red
    unclassified: '#6B6F7F',      // = colors.categories.KU: the "no category yet" strip is KU's hue by design
  },
  shadows: {
    card: '0 1px 3px rgba(0, 0, 0, 0.1)',
    cardHover: '0 2px 8px rgba(0, 0, 0, 0.15)',
    // BentoCard default: navy-tinted soft lift (boundary signal w/o border chrome)
    cardLifted: '0 4px 12px rgba(6, 16, 90, 0.07), 0 1px 3px rgba(6, 16, 90, 0.05)',
    // BentoCard hover: stronger lift — same direction, more intensity
    cardLiftedHover: '0 8px 20px rgba(6, 16, 90, 0.12), 0 2px 4px rgba(6, 16, 90, 0.08)',
  },
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  borderRadius: { sm: 4, md: 8, lg: 12 },
  // Named layering token — InfoTooltip is the one component so far that needs an explicit "always on
  // top" z-index; every other overlay in the app (modals, toasts, sticky nav) still hand-rolls its
  // own literal (100-1200 range) rather than sharing a token. tooltip stays above all of them.
  zIndex: { tooltip: 9999 },
  font: { family: "'Poppins', sans-serif" },
};
